import {
  Breadcrumb,
  Button,
  Card,
  Input,
  Typography,
  DatePicker,
  Tooltip,
  Dropdown,
  message,
  Grid,
} from "antd";
import React, { useEffect, useState } from "react";
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  FilterOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  FilePdfOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { style } from "../../Styles";
import { t } from "i18next";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";

const { Text } = Typography;
function Header({
  addButtonText,
  onAddButtonClick,
  onSearch,
  searchPlaceholder,
  addButtonBackgroundColor,
  addButtonBorderColor,
  addButtonTextColor,
  onReload,
  showAddButton,
  BreadcrumbItems,
  showBreadcrumb,
  showFilterButton,
  onClickFilterButton,
  showExportCSV,
  csvData: initialCsvData, // Renamed to avoid confusion
  csvHeaders: initialCsvHeaders,
  generateCSVfn,
  csvDataMiddleFileName,
  showFilterByDate = false,
  onFilterByDate,
  dateRange,
  showSummaryButton,
  onClickSummaryButton,
  summaryButtonText,
  showChangeStatusButton,
  ChangeStatusButtonText,
  disableChangeStatusButton,
  onClickChangeStatusButton,
  fetchDataPdf
}) {
  const primaryColor = process.env.REACT_APP_PRIMARY_COLOR;
  const secondaryColor = process.env.REACT_APP_SECONDARY_COLOR;
  const { RangePicker } = DatePicker;
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  
  const [csvData, setCsvData] = useState(initialCsvData); // Local state for CSV data
  const [csvHeaders, setCsvHeaders] = useState(initialCsvHeaders); // Local state for headers

  // Determine if we're on a small screen
  const isSmallScreen = !screens.lg;
  const isVerySmallScreen = !screens.md;

  // Sync local state with props when they change
  useEffect(() => {
    setCsvData(initialCsvData);
    setCsvHeaders(initialCsvHeaders);
  }, [initialCsvData, initialCsvHeaders]);

  const handleCsvClick = async (e) => {
    e.preventDefault();
    try {
      let exportData = null;
      let exportHeaders = null;
      
      // Call the generate function (if provided) and wait for it to complete.
      // Backwards compatible: some pages call `done()` without returning data/headers
      // and instead rely on the `csvData` / `csvHeaders` props.
      if (typeof generateCSVfn === "function") {
        await new Promise((resolve) => {
          generateCSVfn(e, (data, headers) => {
            exportData = data;
            exportHeaders = headers;
            resolve();
          });
        });
      }

      const finalHeaders =
        Array.isArray(exportHeaders) && exportHeaders.length > 0
          ? exportHeaders
          : Array.isArray(initialCsvHeaders)
          ? initialCsvHeaders
          : [];

      const finalData =
        Array.isArray(exportData) && exportData.length > 0
          ? exportData
          : Array.isArray(initialCsvData)
          ? initialCsvData
          : [];
      
      // Use the returned data directly instead of waiting for state updates
      if (finalData.length > 0 && finalHeaders.length > 0) {
        // Manually create and download CSV
        const csvContent = [
          finalHeaders.map(h => `"${h.label}"`).join(','),
          ...finalData.map(row => 
            finalHeaders.map(h => {
              const value = row[h.key];
              // Escape quotes in values
              const escaped = String(value || '').replace(/"/g, '""');
              return `"${escaped}"`;
            }).join(',')
          )
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `export-${csvDataMiddleFileName}-${new Date().toISOString().slice(0, 30)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        message.error("No data available for export");
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
      message.error("Error exporting CSV");
    }
  };

  const onClickDropdown = async ({key}) => {
    
    if(key === "1"){
      if(fetchDataPdf != null) {
        const data = await fetchDataPdf();
        if (data) { // Ensure data exists before exporting
          exportToPDF(data);
        } else {
          message.warning(t("drawerButtons.errorExportPdf"));
        }
      } else if(csvData != null){
        exportToPDF(csvData);
      }
    } else if (key === "2") {
      handleCsvClick(new Event('click'));
    }
  };

  const exportToPDF = (csvData) => {
    const doc = new jsPDF('landscape'); // Set orientation to landscape

    // Set the title
    const title = BreadcrumbItems[0]?.title;
    const pageWidth = doc.internal.pageSize.getWidth(); // Get page width
    const textWidth = doc.getTextWidth(title); // Get text width
    const xPosition = (pageWidth - textWidth) / 2; // Center the title
    doc.setFontSize(16); // Set font size
    doc.text(title, xPosition, 20); // X = 14, Y = 20 (position on the page)
    // Extract table headers from csvHeaders
    const tableColumn = csvHeaders.map((col) => col.label);
  
    // Extract table rows from data
    const tableRows = csvData.map((row) => csvHeaders.map((col) => row[col.key]));
  
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 30,
    });

    //
    const filename = csvDataMiddleFileName+"-"+dayjs().format('DD-MM-YYYY-hh-mm') +".pdf";
    doc.save(filename);
  };
  

  const exportMenuItems = [
    {
      key: "1",
      label: t("drawerButtons.exportPdf"),
      icon: <FilePdfOutlined />
    },
    {
      type: "divider",
    },
    {
      key: "2",
      label: t("drawerButtons.exportCsv"),
      icon: <FileExcelOutlined size={100} />
    },
  ];


  return (
    <Card style={style?.headerCard}>
      <div style={style.headerContainer}>
        {showBreadcrumb && BreadcrumbItems && BreadcrumbItems.length > 0 && (
          <Breadcrumb style={style.headerBreadcrumb} items={BreadcrumbItems} />
        )}
        <div style={style.leftItems}>
          <Button
            onClick={() => {
              if (onReload) {
                onReload();
              }
            }}
            style={{
              ...style.headerButton,
              //float: "left",
            }}
            size="large"
          >
            <ReloadOutlined />
          </Button>
          {showChangeStatusButton && <Button
            onClick={() => {
              onClickChangeStatusButton()
            }}
            style={{
              ...style.headerButton,
              //float: "left",
            }}
            size="large"
            disabled={disableChangeStatusButton}
          >
            {ChangeStatusButtonText}
          </Button>}
        </div>

        {showFilterByDate && (
          <div style={{ ...style.centerItem }}>
            <RangePicker
              style={{ ...style.headerButton }}
              value={dateRange}
              onChange={onFilterByDate}
              format={'DD-MM-YYYY'}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              allowClear={false}
              placeholder={[
                t("drawerFilter.startDate"),
                t("drawerFilter.endDate"),
              ]}
            />
          </div>
        )}
        <div style={style.rightItems}>
          {showAddButton && (
            <Button
              onClick={onAddButtonClick}
              style={{
                ...style.headerButton,
                backgroundColor: addButtonBackgroundColor || primaryColor,
                color: addButtonTextColor || "#ffffff",
                float: "right",
              }}
              size="large"
              icon={<PlusOutlined />}
            >
              <Text
                strong
                style={{ ...style.headerButtonText, color: addButtonTextColor }}
              >
                {addButtonText}
              </Text>
            </Button>
          )}
          {showSummaryButton && (
            <Tooltip title={summaryButtonText}>
              <Button
                onClick={onClickSummaryButton}
                style={{
                  ...style.headerButton,
                  backgroundColor: "#ffffff",
                  color: primaryColor,
                  float: "right",
                }}
                size="large"
                icon={<InfoCircleOutlined />}
              >{summaryButtonText}</Button>
            </Tooltip>
          )}
          {showFilterButton && (
            <Button
              icon={<FilterOutlined />}
              onClick={onClickFilterButton}
              style={{
                ...style.headerButton,
                backgroundColor: addButtonBackgroundColor || primaryColor,
                color: addButtonTextColor || "#ffffff",
                float: "right",
              }}
              size="large"
            >
              {t("drawerButtons.filter")}
            </Button>
          )}
          {showExportCSV && (
            <Dropdown menu={{ items: exportMenuItems, onClick: onClickDropdown}}>
                <Button
                icon={<FileOutlined />}
                size="large"
                style={{
                  ...style.headerButton,
                  color: primaryColor,
                  float: "right",
                }}
                onClick={(e) => e.preventDefault()}
              >
                {t("drawerButtons.export")}
              </Button>
              
            </Dropdown>
          )}
          <div
            style={
              {
                /*...style.leftItems,  width: "100%" */
              }
            }
          >
            <Input
              onChange={onSearch}
              size="large"
              placeholder={searchPlaceholder}
              style={style.headerInput}
              prefix={<SearchOutlined />}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}

export default Header;
